import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { type PublicBoard } from '@air/api/types';

import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { type GalleryBoardProps } from '~/components/v2/components/shared/gallery/GalleryBoard';
import { usePublicLibraryCustomFields } from '~/components/v2/hooks/usePublicLibraryCustomFields';
import { useOpenPublicBoardInNewTab } from '~/hooks/useOpenPublicBoardInNewTab';
import { useURLLibraryIdSelector } from '~/hooks/useURLLibraryIdSelector';
import { type PublicBoardContextValue } from '~/providers/PublicBoardProvider';
import { centralizedBoardLibraryIdSelector } from '~/store/centralizedBoard/selectors';
import { canSeeBoardCustomFields } from '~/utils/permissions/boardPermissions';

export type UsePublicGalleryBoardCardPropsParams = {
  board: PublicBoard;
  /**
   * The drill a click performs, supplied by the hosting tree (the collection page, the standalone
   * board page, the editor canvas). A card can't resolve this itself — where "into this board"
   * goes is the hosting surface's business (route push vs editor state), so the drill arrives
   * with the card renderer rather than being read from any viewer-wide contract.
   */
  onNavigateToBoard: PublicBoardContextValue['onNavigateToBoard'];
};

export type PublicGalleryBoardCardProps = Pick<
  GalleryBoardProps<PublicBoard>,
  'canViewCustomFields' | 'libraryCustomFields' | 'onClick' | 'onCmdClickTitle'
>;

/**
 * The public board card's BODY contract — every GalleryBoard prop that shapes what the card looks
 * like and where clicking it goes. Look is derived internally from the public permission and
 * custom-field hooks; the click's drill destination arrives as a param (see
 * `onNavigateToBoard` above). Shared by PublicGalleryBoard (public pages) and
 * ShowcaseEditorGalleryBoard (the editor's edit canvas) so the canvas card stays
 * pixel-identical to the public card (WYSIWYG); only the menu getters — deliberately NOT part of
 * this contract — differ between the two.
 */
export const usePublicGalleryBoardCardProps = ({
  board,
  onNavigateToBoard,
}: UsePublicGalleryBoardCardPropsParams): PublicGalleryBoardCardProps => {
  const { permissions } = usePublicPermissions();
  const { openPublicBoardInNewTab } = useOpenPublicBoardInNewTab();
  const urlLibraryId = useURLLibraryIdSelector();
  const libraryId = useSelector(centralizedBoardLibraryIdSelector);
  const { data: libraryCustomField } = usePublicLibraryCustomFields({
    libraryId: libraryId || urlLibraryId,
  });

  const onClick = useCallback(
    () => onNavigateToBoard({ boardId: board.id, boardTitle: board.title }),
    [board, onNavigateToBoard],
  );

  const onCmdClickTitle = useCallback(() => openPublicBoardInNewTab({ board }), [board, openPublicBoardInNewTab]);

  const libraryCustomFields = useMemo(() => {
    return (libraryCustomField?.data ?? []).map((customField) => ({
      ...customField,
      visible: !!customField.visible,
    }));
  }, [libraryCustomField]);

  return {
    canViewCustomFields: canSeeBoardCustomFields(permissions),
    libraryCustomFields,
    onClick,
    onCmdClickTitle,
  };
};
